import CardGridView from "@/components/CardGridView";
import { Flag, Pencil } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import type { Feature } from "@/lib/models";
import { Code, CodeService } from "@/services/CodeService";
import { featurePriorityColour } from "@/lib/task-utils";
import { featureUrlBuilder } from "@/lib/url-builder";
import DeleteDialog from "../DeleteDialog";
import { FeatureService } from "@/services/FeatureService";
import { Loading } from "../Loading";

interface FeatureDetails {
  id: string;
  name: string;
  description: string | null;
  priorityCode: Code;
  currentCondition  : Code;
}

interface FeatureCardGridViewProps {
  pageTitle: string;
  featureList: Feature[];
  isLoading: boolean;
}

const FeatureCardGridView: React.FC<FeatureCardGridViewProps> = ({ pageTitle, featureList, isLoading }) => {
  const params = useParams();
  const [features, setFeatures] = useState<FeatureDetails[]>([]);
  const [deletedFeatureId, setDeletedFeatureId] = useState<string | null>(null);
  const [showDeleteFeatureDialog, setShowDeleteFeatureDialog] = useState(false);

  const [codes, setCodes] = useState<Code[]>([]);

  useEffect(() => {
    CodeService.getCodes2().then(setCodes);
  }, [])

  useEffect(() => {
    if (codes.length === 0 || featureList.length === 0) {
      return;
    }
    const record: Record<string, Code> = codes.reduce((acc, item) => {
      acc[item.id] = item;
      return acc;
    }, {} as Record<string, Code>);
    setFeatures(
      featureList
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((feature) => ({
          id: feature.id!,
          name: feature.name,
          description: feature.description ?? null,
          priorityCode: record[feature.priorityCodeId!] ?? {
            id: "",
            name: "N/A",
            type: "N/A",
          },
          currentCondition: record[feature.currentConditionCodeId!] ?? {
            id: "",
            name: "N/A",
            type: "N/A",
          },
        }))
    );
  }, [featureList, codes]);

  return (
    <>
      <Loading isLoading={isLoading} message="Loading features..." />
      <CardGridView
        title={pageTitle}
        editPageUrl={(site: string, id: string) =>
          featureUrlBuilder(params.tenantId!, site, id)
        }
        items={features}
        onDeleteItem={async (feature) => {
          setDeletedFeatureId(feature.id)
          setShowDeleteFeatureDialog(true);
        }}
        renderContentItem={(item) => (
          <div className="space-y-1">
            <div className="flex items-center gap-4 text-sm text-gray-600">
              <div className="flex items-center gap-2">{item.description}</div>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                 <Flag className="h-4 w-4 text-gray-400" />
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium`}>
                  {item.currentCondition?.name}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Flag className="h-4 w-4 text-gray-400" />
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${featurePriorityColour[item.priorityCode!.id]}`}>
                  {item.priorityCode?.name}
                </span>
              </div>
              <div className="flex items-center gap-2">
              </div>
            </div>
          </div>
        )}
        tableHeaders={[
          { id: "name", child: <>Name</> },
          { id: "description", child: <>Description</> },
          { id: "currentCondition", child: <><Flag className="inline-block mr-1 h-4 w-4" /> Current Condition</> },
          { id: "priority", child: <><Flag className="inline-block mr-1 h-4 w-4" /> Priority</> },
          { id: "actions", child: "Edit" },
        ]}
        tableContentItem={(feature) => [
          <span key="name">{feature.name}</span>,
          <span key="description">{feature.description}</span>,
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium`}>
        {feature.currentCondition?.name}
        </span>,
        <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            featurePriorityColour[feature.priorityCode!.id]
        }`}
        >
        {feature.priorityCode?.name}
        </span>,
          <Link to={featureUrlBuilder(params.tenantId!, params.siteId!, feature.id!)}>
            <Pencil key="actions" className="h-4 w-4" aria-label={`Edit Area`} />
          </Link>
          
        ]}
      />      
      <DeleteDialog 
        onDelete={async () => {
          if (!deletedFeatureId) {
            alert("No feature selected for deletion");
            return;
          }
          await FeatureService.delete(deletedFeatureId)
          setFeatures(features.filter(a => a.id !== deletedFeatureId))
          setDeletedFeatureId(null)
        }} 
        deleteConfirmationText={<p>Are you sure you want to delete this feature?</p>} 
        isOpen={showDeleteFeatureDialog}
        onOpenChange={setShowDeleteFeatureDialog}
      />
    </>
  );
};

export default FeatureCardGridView;
